import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FileText, Users, Star, Eye, PlusCircle } from 'lucide-react';
import { apiFetch } from '../../utils/api';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiFetch('/stats')
      .then(r => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);

  const statCards = stats ? [
    { label: 'Total Articles', value: stats.articles, icon: FileText, color: 'bg-[#0A1128]' },
    { label: 'Published', value: stats.published, icon: Eye, color: 'bg-[#0A1128]/80' },
    { label: 'Subscribers', value: stats.audience, icon: Users, color: 'bg-[#0A1128]/60' },
    { label: 'Pending Reviews', value: stats.pending_testimonials, icon: Star, color: 'bg-[#C9A96E]' },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <p className="overline text-[#6B7280] mb-2">Welcome back</p>
        <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#0A1128] tracking-tight">
          {user?.name || 'Admin'}
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {statCards.map((s, i) => (
          <div key={i} className="border border-[#E5E7EB] p-6" data-testid={`stat-card-${i}`}>
            <s.icon className="w-5 h-5 text-[#6B7280] mb-3" />
            <p className="font-['Playfair_Display'] text-3xl font-bold text-[#0A1128]">{s.value}</p>
            <p className="text-[#6B7280] text-xs uppercase tracking-widest mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0A1128] mb-6">Quick Actions</h2>
      <div className="grid md:grid-cols-3 gap-4">
        <Link
          to="/admin/articles/new"
          className="border border-[#E5E7EB] p-6 hover:border-[#0A1128] transition-colors group"
          data-testid="create-article-link"
        >
          <PlusCircle className="w-6 h-6 text-[#0A1128] mb-3" />
          <h3 className="font-medium text-[#0A1128] mb-1">Create Article</h3>
          <p className="text-[#6B7280] text-sm">Build a new article with products</p>
        </Link>

        <Link
          to="/admin/audience"
          className="border border-[#E5E7EB] p-6 hover:border-[#0A1128] transition-colors group"
          data-testid="view-audience-link"
        >
          <Users className="w-6 h-6 text-[#0A1128] mb-3" />
          <h3 className="font-medium text-[#0A1128] mb-1">Audience Data</h3>
          <p className="text-[#6B7280] text-sm">View subscriber information</p>
        </Link>

        <Link
          to="/admin/testimonials"
          className="border border-[#E5E7EB] p-6 hover:border-[#0A1128] transition-colors group"
          data-testid="manage-testimonials-link"
        >
          <Star className="w-6 h-6 text-[#0A1128] mb-3" />
          <h3 className="font-medium text-[#0A1128] mb-1">Testimonials</h3>
          <p className="text-[#6B7280] text-sm">Approve and manage reviews</p>
        </Link>
      </div>
    </div>
  );
}
